import { ContentTypeRichText } from '@aspendental/shared-components-web';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Alert, ButtonBase, Grid, Skeleton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { FC, useCallback, useMemo, useState } from 'react';
import { Mousewheel, Navigation, Scrollbar } from 'swiper/modules';
// eslint-disable-next-line import/no-unresolved
import { Swiper, SwiperSlide } from 'swiper/react';
import { getSwiperStyles, noSlotsAlertStyles } from './SlotList.styles';
import useAppointmentInformationStore from '@src/store/appointment-information.store';
interface SlotListProps {
  loading: boolean;
  slots: any;
  title: string;
  noSlotsMsg: any;
  swiperId: string;
}
interface NoSlotsAlertProps {
  msg: any;
}
export const NoSlotsAlert: FC<NoSlotsAlertProps> = ({
  msg
}) => <Alert variant="flatPrimary" severity="info" sx={noSlotsAlertStyles.alertStyles} data-sentry-element="Alert" data-sentry-component="NoSlotsAlert" data-sentry-source-file="SlotList.tsx">
		<ContentTypeRichText content={msg} backgroundColor={'transparent'} disablePaddingOn={['Top', 'Bottom']} disableGutters containerSx={noSlotsAlertStyles.richTextStyles} data-sentry-element="ContentTypeRichText" data-sentry-source-file="SlotList.tsx" />
	</Alert>;
const LoadingSlots = () => <>
		{Array.from({
    length: 4
  }).map((_, index) => <Skeleton key={index} variant="rounded" width={105} height={52} />)}
	</>;
const rightContainerGradient = 'linear-gradient(to right, black 15% 85%, transparent 98%)';
const leftContainerGradient = 'linear-gradient(to right, transparent 2%, black 15% 85%)';
const bothEndsContainerGradient = 'linear-gradient(to right, transparent 2%, black 15% 85%, transparent 98%)';
const SlotList: FC<SlotListProps> = ({
  loading = true,
  slots = [],
  title = '',
  noSlotsMsg,
  swiperId = ''
}) => {
  const theme = useTheme();
  const [swiperContainerGradient, setSwiperContainerGradient] = useState<string | null>(null);
  const {
    setChosenAppointmentData,
    setChosenAppointmentKey,
    chosenAppointmentKey
  } = useAppointmentInformationStore();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const chevrons = useMemo(() => {
    const right = encodeURIComponent(`
			<svg xmlns="http://www.w3.org/2000/svg" fill="${theme.palette.text.interactive}" viewBox="0 0 24 24" width="28" height="28">
				<path d="M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
			</svg>
		`);
    const left = encodeURIComponent(`
			<svg xmlns="http://www.w3.org/2000/svg" fill="${theme.palette.text.interactive}" viewBox="0 0 24 24" width="28" height="28">
				<path d="M15.41 7.41 10.83 12l4.58 4.59L14 18l-6-6 6-6z"/>
			</svg>
		`);
    return {
      left,
      right
    };
  }, [theme]);
  const setLeftGradient = useCallback(() => {
    setSwiperContainerGradient(leftContainerGradient);
  }, []);
  const setRightGradient = useCallback(() => {
    setSwiperContainerGradient(rightContainerGradient);
  }, []);
  const setLeftAndRightGradients = useCallback(() => {
    setSwiperContainerGradient(bothEndsContainerGradient);
  }, []);

  // Early return - Don't show date if no appointments on that date
  if (Object.keys(slots).length === 0) return;
  return <Grid container direction="column" sx={{
    width: '100%',
    maxWidth: {
      xl: '850px',
      lg: '820px',
      md: '480px',
      xs: '100%'
    },
    mb: 3,
    mr: {
      sm: '0'
    }
  }} data-test-id="grid_item_timeslots_message" data-sentry-element="Grid" data-sentry-component="SlotList" data-sentry-source-file="SlotList.tsx">
			<Grid item sx={{
      mb: 1
    }} data-sentry-element="Grid" data-sentry-source-file="SlotList.tsx">
				<Typography variant="bodyLargeSemiBold" sx={{
        md: {
          px: '30px'
        }
      }} color="text.primary" data-sentry-element="Typography" data-sentry-source-file="SlotList.tsx">
					{title}
				</Typography>
			</Grid>

			<Stack direction="row" justifyContent="flex-start" alignItems="flex-start" position="relative" spacing={1} sx={{
      width: '100%',
      overflow: 'hidden'
    }} data-sentry-element="Stack" data-sentry-source-file="SlotList.tsx">
				{loading ? <LoadingSlots /> : slots.length < 1 ? <NoSlotsAlert msg={noSlotsMsg} /> : <Grid item sx={getSwiperStyles(isSmallScreen, slots, swiperContainerGradient, {
        chevronLeft: chevrons?.left,
        chevronRight: chevrons?.right
      })}>
						<Swiper id={swiperId} data-test-id="swiper-section" uniqueNavElements modules={[Navigation, Scrollbar, Mousewheel]} breakpoints={{
          0: {
            slidesPerView: 3.2
          },
          390: {
            slidesPerView: 3.5
          },
          430: {
            slidesPerView: 3.9
          },
          545: {
            slidesPerView: 4.2
          },
          675: {
            slidesPerView: 5.2
          },
          785: {
            slidesPerView: 6.2
          },
          900: {
            slidesPerView: 3.8
          },
          1000: {
            slidesPerView: 4
          },
          1200: {
            slidesPerView: 5.2
          },
          1320: {
            slidesPerView: 6.5
          },
          1535: {
            slidesPerView: 6.9
          }
        }} spaceBetween={16} navigation scrollbar style={{
          width: '100%',
          padding: isMediumScreen ? '0' : '0 30px'
        }} onInit={() => {
          isSmallScreen && slots.length > 3 && setRightGradient();
        }} onActiveIndexChange={swiper => {
          isSmallScreen && swiper.isBeginning ? setRightGradient() : isSmallScreen && swiper.isEnd ? setLeftGradient() : isSmallScreen && setLeftAndRightGradients();
        }}>
							{Object.entries(slots).map(([key, timeSlot], index) => (timeSlot as any)?.open_slots?.length > 0 && <SwiperSlide key={index}>
											<ButtonBase onClick={() => {
              setChosenAppointmentKey(key);
              setChosenAppointmentData({
                timeSlot
              });
            }} sx={{
              border: chosenAppointmentKey === key ? '2px solid' : '1px solid',
              borderColor: chosenAppointmentKey === key ? 'text.primary' : 'neutrals.primary',
              borderRadius: '8px',
              width: {
                sm: '105px',
                xs: '86px'
              },
              height: {
                sm: '52px',
                xs: '46px'
              },
              textAlign: 'center',
              position: 'relative'
            }}>
												{chosenAppointmentKey === key && <CheckCircleIcon sx={{
                color: 'text.primary',
                width: '16px',
                height: '16px',
                position: 'absolute',
                top: '1px',
                right: '1px'
              }} />}
												<Typography variant={isSmallScreen ? 'bodySmallBook' : 'bodyMediumBook'} sx={{
                color: 'text.primary'
              }}>
													{(timeSlot as any).time}
												</Typography>
											</ButtonBase>
										</SwiperSlide>)}
						</Swiper>
					</Grid>}
			</Stack>
		</Grid>;
};
export default SlotList;